/**
 * TrelloCardDetailSection — renders the rich Trello mirror payload
 * for a card whose `provider === 'trello'`. Mounted inside the
 * standard CardDetailDrawer (qf!2785) just below the description so
 * the operator sees one unified detail panel regardless of card
 * provenance.
 *
 * Surfaces:
 *   - Members assigned (avatars + names)
 *   - Labels (Trello named-colour pills)
 *   - Attachments (download links + size)
 *   - Checklists with progress bars
 *   - Comments thread (newest first)
 *   - Activity log (collapsed by default, expand for full timeline)
 *
 * Read-only. Editing pushes to the backend write-back path that
 * lands in a follow-up MR; today the operator continues to author
 * in Trello and our 10-min delta sync brings the changes back.
 *
 * Data source: GET /api/trello/cards/<external_id>/ via
 * `KanbanApi.fetchTrelloCardDetail()` (qb!2754). One round-trip
 * per drawer open; cached under TanStack key
 * `['trello-card-detail', externalId]` so re-opening the same
 * card within the cache stale window is instant.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Activity, AlertCircle, CheckSquare, ChevronDown, ChevronRight,
  ExternalLink, Loader2, MessageSquare, Paperclip, Tag as TagIcon,
  Users,
} from 'lucide-react'

import type { KanbanApi } from '../api'
import { humanizeKanbanError } from '../errorCopy'
import type {
  TrelloActivityRow,
  TrelloAttachmentDetail,
  TrelloCardDetail,
  TrelloChecklistDetail,
  TrelloCommentDetail,
  TrelloLabelBrief,
  TrelloMemberBrief,
} from '../types'
import { CommentBody } from './comment-text'

// Trello uses a fixed named-colour palette for labels. Map them to
// the same hexes Trello renders so the chips look identical to the
// operator's mental model. Empty string ('') means a transparent /
// "no colour" label — render as a neutral grey chip.
const TRELLO_LABEL_COLOR_MAP: Record<string, string> = {
  green:    '#61BD4F',
  yellow:   '#F2D600',
  orange:   '#FF9F1A',
  red:      '#EB5A46',
  purple:   '#C377E0',
  blue:     '#0079BF',
  sky:      '#00C2E0',
  lime:     '#51E898',
  pink:     '#FF78CB',
  black:    '#344563',
  green_dark:  '#519839',
  yellow_dark: '#D9B51C',
  orange_dark: '#CD8313',
  red_dark:    '#B04632',
  purple_dark: '#89609E',
  blue_dark:   '#055A8C',
  sky_dark:    '#0098B7',
  lime_dark:   '#4BBF6B',
  pink_dark:   '#C9558F',
  black_dark:  '#091E42',
}

function labelHex(color: string): string {
  if (!color) return '#94A3B8' // neutral grey for unnamed labels
  return TRELLO_LABEL_COLOR_MAP[color] ?? '#94A3B8'
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const now = new Date()
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    if (sameDay) {
      return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
    }
    return d.toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export interface TrelloCardDetailSectionProps {
  externalId: string
  externalUrl?: string | null
  api: KanbanApi
}

export function TrelloCardDetailSection({
  externalId,
  externalUrl,
  api,
}: TrelloCardDetailSectionProps) {
  const enabled = !!api.fetchTrelloCardDetail
  const query = useQuery<TrelloCardDetail>({
    queryKey: ['trello-card-detail', externalId],
    queryFn: () => api.fetchTrelloCardDetail!(externalId),
    enabled,
    // Re-fetch on focus is fine — read-only payload, deltas
    // tracked by the backend's 10-min sync, no risk of trampling
    // operator edits.
    staleTime: 30_000,
  })

  if (!enabled) {
    return (
      <section className="rounded-md border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-200">
        <AlertCircle className="inline h-3 w-3 mr-1" />
        This Quotech build was compiled without the Trello detail
        API client. Tell your administrator to upgrade the
        <code className="mx-1 rounded bg-amber-100 dark:bg-amber-900/40 px-1 py-0.5">@quotech/ui/kanban</code>
        package.
      </section>
    )
  }

  if (query.isLoading) {
    return (
      <section className="flex items-center gap-2 rounded-md border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 p-3 text-xs text-neutral-500">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading Trello detail…
      </section>
    )
  }

  // BUG-2026-07-02-KANBAN-BOARD-ERROR-GATE sibling — error panel only when
  // there is no detail to render, and never the raw transport string.
  if (!query.data) {
    return (
      <section className="flex items-start gap-2 rounded-md border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-3 text-xs text-red-800 dark:text-red-200">
        <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
        <div>
          <div className="font-semibold">Couldn't load Trello detail.</div>
          <div className="mt-0.5 text-[10.5px] opacity-80">
            {humanizeKanbanError(query.error ?? new Error('Unknown error')).message}
          </div>
          {externalUrl && (
            <a
              href={externalUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-1 inline-flex items-center gap-1 text-red-700 dark:text-red-300 underline"
            >
              <ExternalLink className="h-3 w-3" />
              Open in Trello
            </a>
          )}
        </div>
      </section>
    )
  }

  const d = query.data
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
          Trello
        </h3>
        <a
          href={d.short_url || externalUrl || '#'}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 dark:border-neutral-800 px-1.5 py-0.5 text-[10px] text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-900"
        >
          <ExternalLink className="h-3 w-3" />
          Open in Trello
        </a>
      </div>

      <CountStrip counts={d.counts} />

      {d.members.length > 0 && <MembersBlock members={d.members} />}
      {d.labels.length > 0 && <LabelsBlock labels={d.labels} />}
      {d.attachments.length > 0 && <AttachmentsBlock attachments={d.attachments} />}
      {d.checklists.length > 0 && <ChecklistsBlock checklists={d.checklists} />}
      {d.comments.length > 0 && <CommentsBlock comments={d.comments} />}
      <ActivityBlock activity={d.activity} />
    </section>
  )
}

function CountStrip({ counts }: { counts: TrelloCardDetail['counts'] }) {
  const items = [
    { icon: Paperclip,     label: 'attachments', value: counts.attachments,
      hide: counts.attachments === 0 },
    { icon: MessageSquare, label: 'comments',    value: counts.comments,
      hide: counts.comments === 0 },
    { icon: CheckSquare,   label: 'checklist',
      value: counts.checklist_items_total > 0
        ? `${counts.checklist_items_done}/${counts.checklist_items_total}`
        : 0,
      hide: counts.checklist_items_total === 0 },
    { icon: Users,         label: 'members',     value: counts.members,
      hide: counts.members === 0 },
    { icon: TagIcon,       label: 'labels',      value: counts.labels,
      hide: counts.labels === 0 },
    { icon: Activity,      label: 'activity',    value: counts.activity,
      hide: counts.activity === 0 },
  ].filter((i) => !i.hide)
  if (items.length === 0) {
    return (
      <p className="text-[11px] text-neutral-500">No comments, attachments or activity yet.</p>
    )
  }
  return (
    <div className="flex flex-wrap gap-1.5 text-[10.5px] text-neutral-600 dark:text-neutral-400">
      {items.map((it) => (
        <span
          key={it.label}
          className="inline-flex items-center gap-1 rounded-full bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5"
          title={`${it.value} ${it.label}`}
        >
          <it.icon className="h-3 w-3" />
          {it.value}
        </span>
      ))}
    </div>
  )
}

function MembersBlock({ members }: { members: TrelloMemberBrief[] }) {
  return (
    <Block title="Members" icon={<Users className="h-3 w-3" />}>
      <div className="flex flex-wrap gap-1.5">
        {members.map((m) => (
          <span
            key={m.id}
            className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 text-[10.5px] text-neutral-700 dark:text-neutral-200"
            title={`${m.full_name || m.username} (Trello)`}
          >
            <span
              className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[8px] font-bold text-white leading-none"
              aria-hidden="true"
            >
              {(m.initials || m.full_name || m.username || '??').slice(0, 2).toUpperCase()}
            </span>
            {m.full_name || m.username}
          </span>
        ))}
      </div>
    </Block>
  )
}

function LabelsBlock({ labels }: { labels: TrelloLabelBrief[] }) {
  return (
    <Block title="Labels" icon={<TagIcon className="h-3 w-3" />}>
      <div className="flex flex-wrap gap-1">
        {labels.map((l) => (
          <span
            key={l.id}
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
            style={{ background: labelHex(l.color) }}
            title={l.name || '(unnamed)'}
          >
            {l.name || '\u00A0'}
          </span>
        ))}
      </div>
    </Block>
  )
}

function AttachmentsBlock({ attachments }: { attachments: TrelloAttachmentDetail[] }) {
  return (
    <Block title={`Attachments (${attachments.length})`} icon={<Paperclip className="h-3 w-3" />}>
      <ul className="space-y-1">
        {attachments.map((a) => (
          <li key={a.id} className="flex items-center gap-2 rounded border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-[11px]">
            <Paperclip className="h-3 w-3 shrink-0 text-neutral-400" />
            <a
              href={a.url}
              target="_blank"
              rel="noreferrer noopener"
              className="min-w-0 flex-1 truncate text-blue-600 dark:text-blue-400 hover:underline"
              title={a.name}
            >
              {a.name || a.url}
            </a>
            {a.bytes != null && a.bytes > 0 && (
              <span className="shrink-0 text-[10px] text-neutral-500 tabular-nums">
                {formatBytes(a.bytes)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Block>
  )
}

function ChecklistsBlock({ checklists }: { checklists: TrelloChecklistDetail[] }) {
  return (
    <Block title="Checklists" icon={<CheckSquare className="h-3 w-3" />}>
      <div className="space-y-2">
        {checklists.map((cl) => {
          const done = cl.items.filter((i) => i.complete).length
          const total = cl.items.length
          const pct = total > 0 ? Math.round((done / total) * 100) : 0
          return (
            <div key={cl.id} className="rounded border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-2">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="truncate text-[11px] font-semibold text-neutral-800 dark:text-neutral-100">
                  {cl.name}
                </span>
                {total > 0 && (
                  <span className="shrink-0 text-[10px] text-neutral-500 font-mono">
                    {done}/{total} ({pct}%)
                  </span>
                )}
              </div>
              {total > 0 && (
                <div className="mb-1.5 h-1 rounded bg-neutral-200 dark:bg-neutral-800 overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}
              <ul className="space-y-0.5">
                {cl.items.map((it) => (
                  <li key={it.id} className="flex items-start gap-1.5 text-[11px]">
                    <input
                      type="checkbox"
                      checked={it.complete}
                      readOnly
                      disabled
                      className="mt-0.5 h-3 w-3 cursor-not-allowed accent-emerald-600 opacity-70"
                    />
                    <span
                      className={[
                        it.complete
                          ? 'line-through text-neutral-400'
                          : 'text-neutral-700 dark:text-neutral-200',
                      ].join(' ')}
                    >
                      {it.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>
    </Block>
  )
}

function CommentsBlock({ comments }: { comments: TrelloCommentDetail[] }) {
  return (
    <Block title={`Comments (${comments.length})`} icon={<MessageSquare className="h-3 w-3" />}>
      <ul className="space-y-2">
        {comments.map((c) => (
          <li key={c.id} className="rounded border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-2 text-[11px]">
            <div className="mb-1 flex items-center justify-between gap-2 text-[10px] text-neutral-500">
              <span className="font-semibold text-neutral-700 dark:text-neutral-200">
                {c.author?.full_name || c.author?.username || 'Unknown'}
              </span>
              <span className="tabular-nums">{formatDate(c.date)}</span>
            </div>
            <div className="text-neutral-800 dark:text-neutral-100">
              <CommentBody text={c.body} />
            </div>
          </li>
        ))}
      </ul>
    </Block>
  )
}

function ActivityBlock({ activity }: { activity: TrelloActivityRow[] }) {
  const [expanded, setExpanded] = useState(false)
  if (activity.length === 0) return null
  const visible = expanded ? activity : activity.slice(0, 5)
  return (
    <Block title={`Activity (${activity.length})`} icon={<Activity className="h-3 w-3" />}>
      <ul className="space-y-0.5 text-[10.5px]">
        {visible.map((a) => (
          <li key={a.id} className="flex items-start gap-1.5 rounded px-1 py-0.5 hover:bg-neutral-50 dark:hover:bg-neutral-900">
            <span className="w-28 shrink-0 truncate text-neutral-500 tabular-nums">
              {formatDate(a.date)}
            </span>
            <span className="w-32 shrink-0 truncate font-mono text-[10px] text-neutral-600 dark:text-neutral-400">
              {a.type}
            </span>
            <span className="min-w-0 flex-1 truncate text-neutral-700 dark:text-neutral-200">
              {a.actor?.full_name || a.actor?.username || ''}
              {a.text ? ` — ${a.text}` : ''}
            </span>
          </li>
        ))}
      </ul>
      {activity.length > 5 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
        >
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {expanded ? 'Show fewer' : `Show all ${activity.length} activity rows`}
        </button>
      )}
    </Block>
  )
}

function Block({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <h4 className="mb-1.5 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-neutral-500">
        {icon}
        {title}
      </h4>
      {children}
    </div>
  )
}
